/**
 * Brand color helpers — one source of truth so the scattered
 * `color-mix(in srgb, ${primaryColor} 90%, black)` strings can become
 * `getDarkerColor(primaryColor, 0.1)` and never drift again.
 *
 * Since the @mediatechgroup/color adoption (PM db1b87a2) the math lives in
 * that package; this module keeps the names every call site imports and
 * Club Central's configuration of it (the { r, g, b } parse shape, the
 * five-step contrast-safe cap on the package's surfaces). The pre-adoption
 * answers are pinned in src/utils/__tests__/colorUtils.pinned.test.ts. One
 * answer changed on purpose, decision 9 of the package hand-off:
 * getReadableTextColor picks black or white by comparing the two contrast
 * ratios, where the old rule used a fixed luminance cutoff of 0.5 and gave
 * white text to every mid-tone brand at a failing ratio (the orange #d5782d
 * at 3.20:1, the export green #16A34A at 3.30:1). Everything derived (hover,
 * active, soft, the contrast-safe variants, the luminance, ratio and rating)
 * is the same as before for every color, including the old five-step cap on
 * the contrast-safe search, kept through the package's maxSteps option.
 *
 * Everything here is pure: takes hex / rgb strings, returns hex strings or
 * numbers. No DOM, no React. Caller decides where to use the result.
 */
import {
  parseColor as parsePackageColor,
  contrastSafeColor,
  DEFAULT_SURFACES,
} from "@mediatechgroup/color";

export {
  rgbToHex,
  getDarkerColor,
  getLighterColor,
  getLuminance,
  getContrastRatio,
  getReadableTextColor,
  getWcagRating,
} from "@mediatechgroup/color";

// Parse "#RGB" / "#RRGGBB" / rgb(...) into { r, g, b } 0-255.
// Returns null on unparseable input (named colors, hsl, gradients).
export function parseColor(input: string | null | undefined): { r: number; g: number; b: number } | null {
  const c = parsePackageColor(input);
  return c ? { r: c.r, g: c.g, b: c.b } : null;
}

/**
 * If the brand color would be invisible on the current background
 * (contrast < 3.0), return a contrast-adjusted variant; otherwise
 * return the brand color as-is. Used to keep brand-painted buttons
 * legible in dark mode when the tenant picked something near-black.
 *
 *   getContrastSafeBrandColor("#1f2937", { isDark: true })  → lighter variant
 *   getContrastSafeBrandColor("#ff6600", { isDark: true })  → unchanged
 *
 * Steps 8% at a time, at most five steps (a 34% move), so a near-black
 * brand never comes back as plain white: the pre-adoption cap, kept.
 */
export function getContrastSafeBrandColor(
  brand: string | null | undefined,
  opts: { isDark: boolean },
): string {
  return contrastSafeColor(brand, opts.isDark ? DEFAULT_SURFACES.dark : DEFAULT_SURFACES.light, {
    maxSteps: 5,
  });
}
